import { LON_ABI, LON_STAKING_ABI } from '../constants/abis/index.js';
import { lonAddress, lonStakingAddress } from '../constants/addresses.js';
import { ContextHost } from '../service/contextHost.js';
import {} from './type.js';
import { encodeFunctionData, parseSignature, parseUnits, } from 'viem';
export class Signer extends ContextHost {
    constructor(config, injected) {
        super({ env: config.env, chainId: config.chainId }, {
            publicClient: injected.publicClient,
            walletClient: injected.walletClient,
        });
    }
    async signStake(account, chainId, nonce, spender, value, deadline) {
        const domain = {
            name: 'Tokenlon',
            version: '1',
            chainId: chainId,
            verifyingContract: lonAddress[chainId],
        };
        const types = {
            Permit: [
                { name: 'owner', type: 'address' },
                { name: 'spender', type: 'address' },
                { name: 'value', type: 'uint256' },
                { name: 'nonce', type: 'uint256' },
                { name: 'deadline', type: 'uint256' },
            ],
        };
        const primaryType = 'Permit';
        const message = {
            owner: account,
            spender,
            value,
            nonce,
            deadline,
        };
        const walletClient = this.useWalletClient();
        const signatureTypedData = await walletClient.signTypedData({
            account,
            domain,
            types,
            primaryType,
            message,
        });
        return signatureTypedData;
    }
    async processEthTxOptions(rawTransaction, options) {
        rawTransaction.type = 'eip1559';
        const { maxFeePerGas = 0n, maxPriorityFeePerGas } = await this.publicClient.estimateFeesPerGas();
        const adjustedMaxFeePerGas = (maxFeePerGas * 120n) / 100n;
        rawTransaction.maxFeePerGas = adjustedMaxFeePerGas;
        rawTransaction.maxPriorityFeePerGas = maxPriorityFeePerGas;
        if (typeof options?.afterProcessEthTx === 'function') {
            return options.afterProcessEthTx(rawTransaction);
        }
        return rawTransaction;
    }
    async sendTransaction(rawTransaction) {
        const walletClient = this.useWalletClient();
        const hash = await walletClient.sendTransaction({
            ...rawTransaction,
            account: rawTransaction.from,
        });
        return hash;
    }
    async sendStakeWithPermit(amount, options) {
        const walletClient = this.useWalletClient();
        const [account] = await walletClient.getAddresses();
        const chainId = this.chainId;
        const nonce = await this.publicClient.readContract({
            address: lonAddress[chainId],
            abi: LON_ABI,
            functionName: 'nonces',
            args: [account],
        });
        const deadline = Math.ceil(Date.now() / 1000) + 60 * 60 * 24 * 12;
        const stakeAmount = parseUnits(amount, 18).toString();
        const signatureTypedData = await this.signStake(account, chainId, nonce, lonStakingAddress[chainId], stakeAmount, deadline);
        const sig = parseSignature(signatureTypedData);
        const data = encodeFunctionData({
            abi: LON_STAKING_ABI,
            functionName: 'stakeWithPermit',
            args: [
                BigInt(stakeAmount),
                BigInt(deadline),
                Number(sig.v),
                sig.r,
                sig.s,
            ],
        });
        let rawTransaction = {
            to: lonStakingAddress[chainId],
            from: account,
            data: data,
            value: 0n,
        };
        rawTransaction = await this.processEthTxOptions(rawTransaction, options);
        return rawTransaction;
    }
    async sendUnstake(options) {
        const walletClient = this.useWalletClient();
        const [account] = await walletClient.getAddresses();
        const chainId = this.chainId;
        const data = encodeFunctionData({
            abi: LON_STAKING_ABI,
            functionName: 'unstake',
            args: [],
        });
        let rawTransaction = {
            to: lonStakingAddress[chainId],
            from: account,
            data: data,
            value: 0n,
        };
        rawTransaction = await this.processEthTxOptions(rawTransaction, options);
        return rawTransaction;
    }
    async sendRedeem(amount, options) {
        const walletClient = this.useWalletClient();
        const [account] = await walletClient.getAddresses();
        const chainId = this.chainId;
        const redeemAmount = parseUnits(amount, 18);
        const data = encodeFunctionData({
            abi: LON_STAKING_ABI,
            functionName: 'redeem',
            args: [redeemAmount],
        });
        let rawTransaction = {
            to: lonStakingAddress[chainId],
            from: account,
            data: data,
            value: 0n,
        };
        rawTransaction = await this.processEthTxOptions(rawTransaction, options);
        return rawTransaction;
    }
    async sendRageExit(options) {
        const walletClient = this.useWalletClient();
        const [account] = await walletClient.getAddresses();
        const chainId = this.chainId;
        const data = encodeFunctionData({
            abi: LON_STAKING_ABI,
            functionName: 'rageExit',
            args: [],
        });
        let rawTransaction = {
            to: lonStakingAddress[chainId],
            from: account,
            data: data,
            value: 0n,
        };
        rawTransaction = await this.processEthTxOptions(rawTransaction, options);
        return rawTransaction;
    }
    async signMessage(message) {
        const walletClient = this.useWalletClient();
        const [account] = await walletClient.getAddresses();
        const signature = await walletClient.signMessage({
            account: account,
            message,
        });
        return signature;
    }
}
//# sourceMappingURL=index.js.map